import type { ResourceAttribution } from "@/types_api/backend/transport/get_metric_response"
import type { MetricRecordData } from "@/types/metrics/contracts"

import type { MetricRecord } from "../../runtime"
import { buildMetricRecordIndexById, type MetricLookupInput } from "../identity"
import { convertResponseToMetricRecord } from "./createMetricInstance"

export {
  buildMetricIdToNameLookup,
  buildMetricNameIdLookups,
  buildMetricNameToIdLookup,
  buildMetricRecordIndexById,
} from "../identity"

interface MetricRecordInstanceInput {
  attribution?: ResourceAttribution | null
  id?: string | null
  metric_name: string
  metric_display_name: string
  metric_metadata: MetricRecordData["metric_metadata"]
  description: string
  metric_manager: string
  locked: boolean
  category?: string | null
  created_at?: string
  organization_id?: string | null
  organization_slug?: string
  organization_display_name?: string
  created_by?: string | null
  last_updated_at?: string
  last_updated_by?: string | null
  active?: boolean
  output_type?: string
  organization_threshold?: MetricRecordData["organization_threshold"]
}

export function buildMetricRecordsByKey(
  metricRecordData: Record<string, unknown> | undefined,
): Record<string, MetricRecord> {
  if (!metricRecordData) {
    return {}
  }

  const metricRecords: Record<string, MetricRecord> = {}
  for (const [key, metric] of Object.entries(metricRecordData)) {
    metricRecords[key] = convertResponseToMetricRecord(metric)
  }

  return metricRecords
}

export function buildMetricRecordsByName(
  metricRecordData: Record<string, unknown> | undefined,
): Record<string, MetricRecord> {
  if (!metricRecordData) {
    return {}
  }

  const metricRecords: Record<string, MetricRecord> = {}
  for (const metric of Object.values(metricRecordData)) {
    const record = convertResponseToMetricRecord(metric)
    if (record.metric_name) {
      metricRecords[record.metric_name] = record
    }
  }

  return metricRecords
}

export function buildMetricRecordInstancesById(
  metricRecords: MetricLookupInput<MetricRecordInstanceInput>,
  referenceAliases?: Record<string, string>,
): Record<string, MetricRecord> {
  const metricRecordInstances: Record<string, MetricRecord> = {}

  for (const record of Object.values(metricRecords || {})) {
    if (!record) {
      continue
    }

    const instance = convertResponseToMetricRecord(record)
    if (instance.id) {
      metricRecordInstances[instance.id] = instance
    }
  }

  const recordsById = buildMetricRecordIndexById(metricRecords)
  for (const [referenceId, canonicalId] of Object.entries(
    referenceAliases ?? {},
  )) {
    const record = recordsById[canonicalId]
    if (metricRecordInstances[referenceId] || !record) {
      continue
    }

    // Borrow rendering metadata without moving stored identities or comparing separate version lineages.
    const reference = convertResponseToMetricRecord({
      ...record,
      id: referenceId,
      current_version_id: null,
      current_version_number: null,
    })
    reference.hasComparableVersionLineage = false
    metricRecordInstances[referenceId] = reference
  }

  return metricRecordInstances
}
